import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppApi } from '@/lib/api';
import { getApiRoot } from '@/lib/domainResolver';
import { ADVANCED_DEFAULTS, DEFAULT_PRESET } from './videoPresets';

/**
 * Drives the video compressor: holds the chosen file + settings, uploads to
 * POST /api/utility/video/compress, then polls the UtilityJob row until it is
 * completed/failed. The server owns the ffmpeg run; this hook only reflects the row.
 *
 * Ported from the sibling `billing` app, with its auth swapped out: billing reads a bearer
 * token from localStorage, whereas this app authenticates with an httpOnly JWT cookie — so
 * the XHR sets `withCredentials` and sends no Authorization header.
 */
const POLL_MS = 1500;

export default function useVideoCompression() {
  // fetchApi from useAppApi() is permanently stable by construction (client/src/lib/api.js),
  // so it is safe in dependency arrays — no ref indirection needed here.
  const { fetchApi } = useAppApi();

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mode, setMode] = useState('simple'); // 'simple' | 'advanced'
  const [presetKey, setPresetKey] = useState(DEFAULT_PRESET);
  const [advanced, setAdvanced] = useState({ ...ADVANCED_DEFAULTS });

  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | uploading | pending | processing | completed | failed
  const [progress, setProgress] = useState(0);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  const pollRef = useRef(null);
  const aliveRef = useRef(true);
  const uploadXhrRef = useRef(null);

  useEffect(() => {
    // Re-set on every mount, not just declared true once: React 18 StrictMode runs effects
    // mount → cleanup → remount in dev, so without this the flag stays false after the first
    // cleanup and every post-await `if (!aliveRef.current) return` guard short-circuits —
    // the UI freezes on "Uploading…" even though the upload succeeded.
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearTimeout(pollRef.current);
      if (uploadXhrRef.current) { try { uploadXhrRef.current.abort(); } catch { /* noop */ } }
    };
  }, []);

  // Release the previous object URL whenever the preview changes or the page unmounts.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const chooseFile = useCallback((f) => {
    if (!f) return;
    if (!/^video\//i.test(f.type)) {
      setError('Please choose a video file.');
      return;
    }
    setError(null);
    setFile(f);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    // A new file invalidates any prior result.
    setJobId(null); setStatus('idle'); setProgress(0); setJob(null);
  }, []);

  const poll = useCallback(async (id) => {
    try {
      const res = await fetchApi('crud', { model: 'UtilityJob', action: 'show', where: { id } });
      const row = Array.isArray(res?.data) ? res.data[0] : (res?.data || res);
      if (!aliveRef.current) return;
      if (!row || !row.id) throw new Error('Job not found');
      setJob(row);
      setStatus(row.status);
      setProgress(Number(row.progress_pct) || 0);
      if (row.status === 'completed') return;
      if (row.status === 'failed') { setError(row.error_message || 'Compression failed'); return; }
      pollRef.current = setTimeout(() => poll(id), POLL_MS);
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e?.response?.data?.error || e?.message || 'Lost track of the compression job');
      setStatus('failed');
    }
  }, [fetchApi]);

  const start = useCallback(async () => {
    if (!file) { setError('Choose a video first.'); return; }
    setError(null);
    setStatus('uploading');
    setProgress(0);
    setJob(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (mode === 'simple') form.append('presetKey', presetKey);
      else form.append('advanced', JSON.stringify(advanced));

      // XMLHttpRequest rather than fetch: fetch cannot report upload progress, so a
      // multi-hundred-MB file would sit frozen at 0% with no way to tell uploading from
      // stalled. This is the one place in the app that does not go through fetchApi.
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        uploadXhrRef.current = xhr;
        xhr.open('POST', `${getApiRoot()}/api/utility/video/compress`);
        // The JWT is an httpOnly cookie; without this the request is anonymous and 401s.
        xhr.withCredentials = true;
        xhr.timeout = 30 * 60 * 1000; // 30 min ceiling for very large files

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && aliveRef.current) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        // Body fully sent → the server is now creating the job and starting ffmpeg.
        xhr.upload.onload = () => { if (aliveRef.current) setProgress(100); };

        xhr.onload = () => {
          let body = {};
          try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON error page */ }
          if (xhr.status >= 200 && xhr.status < 300) resolve(body);
          else reject(new Error(body.error || `Upload failed (HTTP ${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload — is the server reachable?'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.onabort = () => reject(new Error('Upload cancelled'));
        xhr.send(form);
      });

      uploadXhrRef.current = null;
      if (!aliveRef.current) return;
      setJobId(data.jobId);
      setStatus('pending');
      setProgress(0); // the same bar now tracks the encode, which starts from zero
      poll(data.jobId);
    } catch (e) {
      uploadXhrRef.current = null;
      if (!aliveRef.current) return;
      setError(e?.message || 'Could not start compression');
      setStatus('failed');
    }
  }, [file, mode, presetKey, advanced, poll]);

  const download = useCallback(async () => {
    if (!jobId) return;
    setError(null);
    try {
      // Mint a short-lived signed URL through the crud channel — reads strip output_url, so
      // this action is the only way to obtain a fetchable handle, and it re-checks ownership.
      const res = await fetchApi('crud', { model: 'UtilityJob', action: 'mintDownload', where: { id: jobId } });
      const url = res?.data?.url || res?.url;
      if (!url) throw new Error(res?.error || 'Could not prepare the download');
      const a = document.createElement('a');
      a.href = `${getApiRoot()}${url}`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Download failed');
    }
  }, [jobId, fetchApi]);

  const reset = useCallback(() => {
    clearTimeout(pollRef.current);
    if (uploadXhrRef.current) { try { uploadXhrRef.current.abort(); } catch { /* noop */ } uploadXhrRef.current = null; }
    setJobId(null); setStatus('idle'); setProgress(0); setJob(null); setError(null);
  }, []);

  const running = status === 'uploading' || status === 'pending' || status === 'processing';

  return {
    file, previewUrl, chooseFile,
    mode, setMode,
    presetKey, setPresetKey,
    advanced, setAdvanced,
    jobId, status, progress, job, error, running,
    start, download, reset,
  };
}
